import React, { useEffect, useCallback, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Building2,
  MapPin,
  Phone,
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Search,
  Sparkles,
  Stethoscope,
  Info,
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from '@/components/ui/phone-input';
import PlacesAutocomplete from '@/components/ui/google-places-autocomplete';
import { State } from '@/models/state.model';
import { stateService } from '@/services/stateService';
import { clinicOnboardingService, NpiLookupResult } from '@/services/clinicOnboardingService';
import { ClinicInfoData } from '../types';
import { useToast } from '@/hooks/use-toast';

const clinicInfoSchema = z
  .object({
    name: z.string().min(1, 'Clinic name is required').max(120, 'Clinic name must be at most 120 characters'),
    npi: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine((val) => (!val || val === '' ? true : /^\d{10}$/.test(val)), {
        message: 'NPI must be exactly 10 digits',
      }),
    address_line_1: z
      .string()
      .min(5, 'Address must be at least 5 characters')
      .max(100, 'Address must be at most 100 characters')
      .trim(),
    address_line_2: z.string().max(100).optional().or(z.literal('')),
    city: z.string().min(2, 'Please enter a valid city'),
    state: z.string().min(2, 'Please select a state'),
    postal_code: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid postal code'),
    primary_phone: z
      .string()
      .min(1, 'Phone number is required')
      .refine((val) => {
        try {
          return isValidPhoneNumber(val, 'US');
        } catch {
          return false;
        }
      }, 'Please enter a valid phone number'),
    fax: z
      .string()
      .optional()
      .or(z.literal(''))
      .refine(
        (val) => {
          if (!val) return true;
          try {
            return isValidPhoneNumber(val, 'US');
          } catch {
            return false;
          }
        },
        { message: 'Invalid fax number' }
      ),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
    description: z
      .string()
      .min(10, 'Description must be at least 10 characters')
      .max(1000, 'Description cannot exceed 1000 characters'),
  })
  .superRefine((data, ctx) => {
    // NPI required for direct archetype is enforced in parent when clinicArchetype is set
    if (data.npi && !/^\d{10}$/.test(data.npi)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'NPI must be exactly 10 digits', path: ['npi'] });
    }
  });

export type ClinicInfoFormData = z.infer<typeof clinicInfoSchema>;

export interface ClinicInfoStepHandle {
  triggerValidation: () => Promise<boolean>;
}

interface ClinicInfoStepProps {
  data: ClinicInfoData & { clinicArchetype?: 'guided' | 'direct' | 'hybrid' };
  updateData: (updates: Partial<ClinicInfoData & { clinicArchetype?: 'guided' | 'direct' | 'hybrid' }>) => void;
  onValidationChange?: (isValid: boolean) => void;
  inviteEmail?: string;
}

export const ClinicInfoStep = forwardRef<ClinicInfoStepHandle, ClinicInfoStepProps>(
  ({ data, updateData, onValidationChange, inviteEmail }, ref) => {
    const [searchParams] = useSearchParams();
    const { toast } = useToast();
    const [states, setStates] = useState<State[]>([]);
    const [npiValidation, setNpiValidation] = useState<{ isValidating: boolean; exists: boolean }>({
      isValidating: false,
      exists: false,
    });
    const [npiLookup, setNpiLookup] = useState<{
      isLoading: boolean;
      result: NpiLookupResult | null;
      hasAutofilled: boolean;
      error: string | null;
    }>({ isLoading: false, result: null, hasAutofilled: false, error: null });
    const token = searchParams.get('token');
    const clinicArchetypeRef = useRef(data.clinicArchetype);
    clinicArchetypeRef.current = data.clinicArchetype;

    const form = useForm<ClinicInfoFormData>({
      resolver: zodResolver(clinicInfoSchema),
      defaultValues: {
        name: data.name || '',
        npi: data.npi || '',
        address_line_1: data.address_line_1 || '',
        address_line_2: data.address_line_2 || '',
        city: data.city || '',
        state: data.state || '',
        postal_code: data.postal_code || '',
        primary_phone: data.primary_phone || '',
        fax: data.fax || '',
        email: data.email || inviteEmail || '',
        description: data.description || '',
      },
      mode: 'onChange',
    });

    useImperativeHandle(ref, () => ({
      triggerValidation: async () => {
        const ok = await form.trigger();
        return ok && !npiValidation.exists && !npiValidation.isValidating;
      },
    }));

    useEffect(() => {
      const hasValues = data.name && data.address_line_1 && data.city;
      if (hasValues) {
        const t = setTimeout(() => form.trigger(), 150);
        return () => clearTimeout(t);
      }
    }, []);

    useEffect(() => {
      stateService.getAllStates().then((res) => {
        if (res.data?.states) setStates(res.data.states);
      });
    }, []);

    const validateNpi = useCallback(async (npi: string) => {
      if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
        setNpiValidation({ isValidating: false, exists: false });
        return;
      }
      setNpiValidation((prev) => ({ ...prev, isValidating: true }));
      try {
        const result = await clinicOnboardingService.checkNpiExists(npi);
        setNpiValidation({ isValidating: false, exists: result.exists });
        if (result.exists) form.setError('npi', { type: 'manual', message: 'A clinic with this NPI already exists' });
        else form.clearErrors('npi');
      } catch {
        setNpiValidation({ isValidating: false, exists: false });
      }
    }, [form]);

    const lastValidatedNpiRef = useRef<string>('');
    const npiTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const npiValue = form.watch('npi');
    useEffect(() => {
      if (npiTimeoutRef.current) clearTimeout(npiTimeoutRef.current);
      if (npiValue && npiValue.length === 10 && /^\d{10}$/.test(npiValue)) {
        if (lastValidatedNpiRef.current !== npiValue) {
          npiTimeoutRef.current = setTimeout(() => validateNpi(npiValue), 500);
        }
      } else {
        setNpiValidation({ isValidating: false, exists: false });
        lastValidatedNpiRef.current = '';
      }
      return () => {
        if (npiTimeoutRef.current) clearTimeout(npiTimeoutRef.current);
      };
    }, [npiValue, validateNpi]);

    const handleNpiLookup = useCallback(async () => {
      const npi = form.getValues('npi');
      if (!npi || !/^\d{10}$/.test(npi)) {
        toast({ title: 'Invalid NPI', description: 'Please enter a valid 10-digit NPI first', variant: 'destructive' });
        return;
      }
      if (!token) {
        toast({ title: 'Error', description: 'Invalid session — please use the link from your email', variant: 'destructive' });
        return;
      }
      setNpiLookup((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const result = await clinicOnboardingService.lookupNpi(npi, token);
        if (!result) {
          setNpiLookup({ isLoading: false, result: null, hasAutofilled: false, error: 'NPI not found' });
          toast({ title: 'NPI Not Found', description: 'Enter information manually.', variant: 'destructive' });
          return;
        }
        setNpiLookup({ isLoading: false, result, hasAutofilled: true, error: null });
        if (result.entity_type === 'organization' && result.organization_name) {
          form.setValue('name', result.organization_name, { shouldValidate: true, shouldDirty: true });
        }
        if (result.address) {
          const addr = result.address;
          if (addr.address_line_1) form.setValue('address_line_1', addr.address_line_1, { shouldValidate: true, shouldDirty: true });
          if (addr.address_line_2) form.setValue('address_line_2', addr.address_line_2, { shouldDirty: true });
          if (addr.city) form.setValue('city', addr.city, { shouldValidate: true, shouldDirty: true });
          if (addr.state) {
            const matched = states.find((s) => s.code === addr.state || s.name?.toLowerCase() === addr.state?.toLowerCase());
            if (matched) form.setValue('state', matched.code, { shouldValidate: true, shouldDirty: true });
          }
          if (addr.postal_code) form.setValue('postal_code', addr.postal_code, { shouldValidate: true, shouldDirty: true });
          if (addr.phone) form.setValue('primary_phone', addr.phone, { shouldValidate: true, shouldDirty: true });
          if (addr.fax) form.setValue('fax', addr.fax, { shouldDirty: true });
        }
        toast({ title: 'NPI Data Found', description: 'Form pre-filled from registry. Edit as needed.', variant: 'success' });
      } catch {
        setNpiLookup({ isLoading: false, result: null, hasAutofilled: false, error: 'Lookup failed' });
        toast({ title: 'Lookup Failed', description: 'Enter information manually.', variant: 'destructive' });
      }
    }, [form, token, states, toast]);

    const handleAddressSelect = useCallback(
      (addressData: { address_line_1?: string; address_line_2?: string; city?: string; state?: string; postal_code?: string }) => {
        if (!addressData) return;
        form.setValue('address_line_1', addressData.address_line_1 || '', { shouldValidate: true, shouldDirty: true });
        form.setValue('address_line_2', addressData.address_line_2 || '', { shouldDirty: true });
        form.setValue('city', addressData.city || '', { shouldValidate: true, shouldDirty: true });
        if (addressData.state) {
          const matched = states.find(
            (s) => s.code === addressData.state || s.name?.toLowerCase() === addressData.state?.toLowerCase()
          );
          form.setValue('state', matched?.code || addressData.state, { shouldValidate: true, shouldDirty: true });
        }
        if (addressData.postal_code) {
          form.setValue('postal_code', addressData.postal_code, { shouldValidate: true, shouldDirty: true });
        }
      },
      [form, states]
    );

    useEffect(() => {
      const subscription = form.watch((values) => {
        updateData({
          name: values.name || '',
          npi: values.npi || '',
          address_line_1: values.address_line_1 || '',
          address_line_2: values.address_line_2 || '',
          city: values.city || '',
          state: values.state || '',
          postal_code: values.postal_code || '',
          primary_phone: values.primary_phone || '',
          fax: values.fax || '',
          email: values.email || '',
          description: values.description || '',
        });
      });
      return () => subscription.unsubscribe();
    }, [form, updateData]);

    useEffect(() => {
      onValidationChange?.(form.formState.isValid && !npiValidation.exists && !npiValidation.isValidating);
    }, [form.formState.isValid, npiValidation, onValidationChange]);

    const clinicArchetype = data.clinicArchetype;

    return (
      <Form {...form}>
        <form className="space-y-6">
          {/* Clinical Setup */}
          <Card className="rounded-xl border border-gray-100 bg-slate-50 shadow-sm">
            <CardContent className="space-y-5 p-4">
              <h3 className="flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider">
                <Stethoscope className="h-4 w-4" />
                Clinical Setup
              </h3>
              <p className="text-xs text-gray-500">
                Help us understand how your clinic operates so we can configure the best experience.
              </p>
              <RadioGroup
                value={clinicArchetype === 'guided' ? 'no' : clinicArchetype ? 'yes' : ''}
                onValueChange={(value: string) => {
                  updateData({ clinicArchetype: value === 'no' ? 'guided' : clinicArchetype === 'guided' ? 'direct' : clinicArchetype || 'direct' });
                }}
                className="grid gap-2"
              >
                <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10">
                  <RadioGroupItem value="yes" />
                  <p className="text-sm font-medium text-gray-900">Yes — we have in-house prescribers</p>
                </label>
                <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer hover:bg-gray-50 has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10">
                  <RadioGroupItem value="no" />
                  <p className="text-sm font-medium text-gray-900">No — we'd like NovaMed to provide clinical oversight</p>
                </label>
              </RadioGroup>
              {clinicArchetype && clinicArchetype !== 'guided' && (
                <div className="space-y-3 border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-900">
                    Would you like to supplement with Nimbus Clinical Support for select programs?
                  </p>
                  <RadioGroup
                    value={clinicArchetype === 'hybrid' ? 'mix' : 'all-inhouse'}
                    onValueChange={(value: string) => updateData({ clinicArchetype: value === 'mix' ? 'hybrid' : 'direct' })}
                    className="grid gap-2"
                  >
                    <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10">
                      <RadioGroupItem value="all-inhouse" />
                      <p className="text-sm font-medium text-gray-900">No — In-house only</p>
                    </label>
                    <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 cursor-pointer has-[:checked]:border-brand-teal has-[:checked]:bg-brand-teal/10">
                      <RadioGroupItem value="mix" />
                      <p className="text-sm font-medium text-gray-900">Yes — Add Nimbus Clinical Support for select programs</p>
                    </label>
                  </RadioGroup>
                </div>
              )}
              {clinicArchetype && (
                <div
                  className={`rounded-xl p-4 text-sm ${
                    clinicArchetype === 'guided'
                      ? 'bg-blue-50 border border-blue-200 text-blue-700'
                      : clinicArchetype === 'hybrid'
                        ? 'bg-amber-50 border border-amber-200 text-amber-700'
                        : 'bg-brand-teal/10 border border-brand-teal/20 text-brand-teal'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <div>
                      {clinicArchetype === 'guided' && (
                        <p><strong>NovaMed Guided Care</strong> — NovaMed will pair your patients with our clinical team.</p>
                      )}
                      {clinicArchetype === 'direct' && (
                        <p><strong>Direct Prescribing</strong> — Your providers will prescribe directly through the platform.</p>
                      )}
                      {clinicArchetype === 'hybrid' && (
                        <p><strong>Flexible Model</strong> — Toggle between in-house and Nimbus Clinical Support per program.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Clinic Information */}
          <Card className="rounded-xl border border-gray-100 bg-slate-50 shadow-sm">
            <CardContent className="space-y-4 p-4">
              <h3 className="flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider">
                <Building2 className="h-4 w-4" />
                Clinic Information
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium text-sm">Clinic Name <span style={{ color: '#ef4444' }}>*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Enter clinic name" maxLength={120} {...field} className="bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="npi"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium text-sm">
                        NPI {clinicArchetype === 'direct' && <span className="text-red-500" style={{ color: '#ef4444' }}>*</span>}
                      </FormLabel>
                      <FormControl>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <Input
                              placeholder={clinicArchetype === 'direct' ? '10-digit NPI' : 'NPI (optional)'}
                              maxLength={10}
                              {...field}
                              className={`bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20 ${npiValidation.exists ? 'border-red-500' : ''}`}
                            />
                            {npiValidation.isValidating && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                              </div>
                            )}
                            {npiValidation.exists && !npiValidation.isValidating && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <AlertCircle className="h-4 w-4 text-red-600" />
                              </div>
                            )}
                            {npiLookup.hasAutofilled && !npiValidation.exists && (
                              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                <CheckCircle2 className="h-4 w-4 text-brand-teal" />
                              </div>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="default"
                            onClick={handleNpiLookup}
                            disabled={npiLookup.isLoading || !field.value || field.value.length !== 10}
                            className="shrink-0 gap-1.5 border-2 border-brand-teal bg-transparent text-brand-teal hover:bg-brand-teal/10"
                          >
                            {npiLookup.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                            <span className="hidden sm:inline">Lookup</span>
                          </Button>
                        </div>
                      </FormControl>
                      {npiLookup.hasAutofilled && (
                        <p className="text-xs text-brand-teal flex items-center gap-1 mt-1">
                          <Sparkles className="h-3 w-3" /> Auto-filled from NPPES
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card className="overflow-visible rounded-xl border border-gray-100 bg-slate-50 shadow-sm">
            <CardContent className="space-y-4 overflow-visible p-4">
              <h3 className="flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider">
                <MapPin className="h-4 w-4" />
                Address
              </h3>
              <FormField
                control={form.control}
                name="address_line_1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <PlacesAutocomplete
                        onSelectPlace={handleAddressSelect}
                        value={field.value}
                        onManualChange={field.onChange}
                        label="Address Line 1"
                        required
                        labelClassName="text-gray-700 font-medium text-sm"
                        inputClassName="bg-white border-gray-200 text-gray-900"
                        suggestionsClassName="bg-white border-gray-200 [&_li]:text-gray-900 [&_li:hover]:bg-gray-100 [&_li_span.ml-1]:text-gray-500"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="address_line_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium text-sm">Address Line 2</FormLabel>
                    <FormControl>
                      <Input placeholder="Building, floor, suite (optional)" {...field} className="bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium text-sm">City <span style={{ color: '#ef4444' }}>*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="City" {...field} className="bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium text-sm">State <span style={{ color: '#ef4444' }}>*</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                            <SelectValue placeholder="State" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {states.map((state) => (
                            <SelectItem key={state.id} value={state.code}>
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="postal_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium text-sm">Postal Code <span style={{ color: '#ef4444' }}>*</span></FormLabel>
                      <FormControl>
                        <Input placeholder="Postal code" maxLength={10} {...field} className="bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="rounded-xl border border-gray-100 bg-slate-50 shadow-sm">
            <CardContent className="space-y-4 p-4">
              <h3 className="flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider">
                <Phone className="h-4 w-4" />
                Contact
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <PhoneInput
                  form={form}
                  name="primary_phone"
                  placeholder="Primary phone"
                  label="Primary Phone"
                  required
                  labelClassName="text-gray-700 font-medium text-sm"
                  inputClassName="!bg-white !border-gray-200 !text-gray-900"
                />
                <PhoneInput
                  form={form}
                  name="fax"
                  placeholder="Fax"
                  label="Fax"
                  isFax
                  required={false}
                  labelClassName="text-gray-700 font-medium text-sm"
                  inputClassName="!bg-white !border-gray-200 !text-gray-900"
                />
              </div>
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium text-sm">Email <span style={{ color: '#ef4444' }}>*</span></FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="Clinic email" {...field} className="bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Description */}
          <Card className="rounded-xl border border-gray-100 bg-slate-50 shadow-sm">
            <CardContent className="space-y-4 p-4">
              <h3 className="flex items-center gap-2 text-[0.8rem] font-semibold uppercase text-brand-teal tracking-wider">
                <FileText className="h-4 w-4" />
                Description
              </h3>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium text-sm">Clinic Description <span style={{ color: '#ef4444' }}>*</span></FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description of your clinic"
                        className="min-h-[100px] bg-white border-gray-200 text-gray-900 focus:border-brand-teal focus:ring-brand-teal/20"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>
        </form>
      </Form>
    );
  }
);

ClinicInfoStep.displayName = 'ClinicInfoStep';
export default ClinicInfoStep;
